import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Bell, AlertTriangle, Sparkles, Radio } from 'lucide-react';

/**
 * LiveAnnouncementsTicker - A professional news-style ticker
 * Features:
 * - Continuous scrolling animation
 * - Works with any number of announcements (1-50+)
 * - Auto-updates when new announcements are added
 * - Handles edge cases (no announcements, single announcement)
 */
const LiveAnnouncementsTicker = ({ announcements = [], onRefresh }) => {
  const tickerRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  const [tickerWidth, setTickerWidth] = useState(0);

  // Calculate animation duration based on content length
  // More announcements = longer duration for readable speed
  const baseDuration = 20; // seconds for base speed
  const durationPerAnnouncement = 5; // additional seconds per announcement
  const animationDuration = Math.max(baseDuration, announcements.length * durationPerAnnouncement);

  // Duplicate announcements to create seamless loop
  // We need at least 2 copies to ensure continuous flow
  const duplicatedAnnouncements = announcements.length > 0 
    ? [...announcements, ...announcements, ...announcements] 
    : [];

  // Measure ticker content width for animation
  useEffect(() => {
    if (tickerRef.current) {
      setTickerWidth(tickerRef.current.scrollWidth / 3); // Divide by 3 since we tripled content
    }
  }, [announcements]);

  // Auto-refresh announcements every 30 seconds
  useEffect(() => {
    if (onRefresh) {
      const interval = setInterval(() => {
        onRefresh();
      }, 30000); // 30 seconds
      return () => clearInterval(interval);
    }
  }, [onRefresh]);

  if (announcements.length === 0) {
    return (
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-slate-800 via-slate-900 to-slate-800 p-4">
        <div className="flex items-center justify-center gap-3 text-slate-400">
          <Bell className="w-5 h-5" />
          <span className="text-sm">No announcements at this time</span>
        </div>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-slate-900 via-blue-950 to-slate-900 shadow-2xl">
      {/* Live indicator */}
      <div className="absolute left-0 top-0 bottom-0 z-20 flex items-center px-4 bg-gradient-to-r from-red-600 to-red-500 shadow-lg">
        <div className="flex items-center gap-2">
          <motion.div
            className="w-2 h-2 bg-white rounded-full"
            animate={{ opacity: [1, 0.3, 1] }}
            transition={{ repeat: Infinity, duration: 1 }}
          />
          <span className="text-white font-bold text-sm tracking-wider">LIVE</span>
        </div>
      </div>

      {/* Gradient fade edges */}
      <div className="absolute left-20 top-0 bottom-0 w-16 bg-gradient-to-r from-slate-900 to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-slate-900 to-transparent z-10 pointer-events-none" />

      {/* Ticker container */}
      <div 
        className="py-4 pl-24 overflow-hidden cursor-pointer"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <motion.div
          ref={tickerRef}
          className="flex items-center whitespace-nowrap"
          animate={{
            x: isPaused ? 0 : [-tickerWidth, 0],
          }}
          transition={{
            x: {
              repeat: Infinity,
              repeatType: "loop",
              duration: animationDuration,
              ease: "linear",
            },
          }}
          style={{ willChange: 'transform' }}
        >
          {duplicatedAnnouncements.map((announcement, index) => (
            <TickerItem 
              key={`${announcement.id}-${index}`} 
              announcement={announcement} 
            />
          ))}
        </motion.div>
      </div>

      {/* Pause indicator */}
      {isPaused && (
        <motion.div 
          className="absolute right-4 top-1/2 -translate-y-1/2 z-20"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm text-white text-xs font-medium">
            Paused
          </div>
        </motion.div>
      )}
    </div>
  );
};

// Individual ticker item
const TickerItem = ({ announcement }) => {
  const isUrgent = announcement.priority === 2 || announcement.category === 'urgent';
  
  return (
    <div className="flex items-center mx-8 flex-shrink-0">
      {/* Separator dot */}
      <div className="w-2 h-2 rounded-full bg-amber-400 mr-6" />
      
      {/* Icon */}
      <div className={`p-2 rounded-lg mr-3 ${
        isUrgent 
          ? 'bg-gradient-to-r from-red-500 to-rose-500' 
          : 'bg-gradient-to-r from-amber-500 to-orange-500'
      }`}>
        {isUrgent ? (
          <AlertTriangle className="w-4 h-4 text-white" />
        ) : (
          <Sparkles className="w-4 h-4 text-white" />
        )}
      </div>
      
      {/* Content */}
      <div className="flex items-center gap-4">
        {announcement.title && (
          <span className={`font-bold ${isUrgent ? 'text-red-400' : 'text-amber-400'}`}>
            {announcement.title}:
          </span>
        )}
        <span className="text-white/90 font-medium">
          {announcement.text}
        </span>
        {announcement.category && (
          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
            announcement.category === 'urgent' ? 'bg-red-500/30 text-red-300' :
            announcement.category === 'event' ? 'bg-purple-500/30 text-purple-300' :
            announcement.category === 'celebration' ? 'bg-emerald-500/30 text-emerald-300' :
            'bg-blue-500/30 text-blue-300'
          }`}>
            {announcement.category.toUpperCase()}
          </span>
        )}
      </div>
    </div>
  );
};

export default LiveAnnouncementsTicker;
